import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App.tsx';
import { HomeView, RoundView } from './views';
import { createBrowserRouter, redirect } from 'react-router';
import { createApolloClient } from '@slope/graphql-adapter';
import { createApolloMockClient } from '@slope/graphql-adapter/apollo/mock';
import { createSlopeKeycloak } from '@slope/keycloak';
import { getToken } from '@slope/keycloak/graphql';
import { ToastContainer, toast } from 'react-toastify';
import { KeycloakProvider, ToastAuth } from '@slope/keycloak/react';
import { isClientCredentials, getDevToken } from 'virtual:kc-dev-token';

// Create keycloak instance
const tokenProvider = {
  isClientCredentials,
  getDevToken,
};

const keycloak = createSlopeKeycloak({
  config: {
    url: import.meta.env.SLOPE_APP_AUTH_URL,
    realm: import.meta.env.SLOPE_APP_AUTH_REALM,
    clientId: import.meta.env.SLOPE_APP_AUTH_CLIENT_ID,
  },
  tokenProvider,
});

const router = createBrowserRouter([
  {
    index: true,
    Component: HomeView,
  },
  {
    path: '/tournament/:tournamentId',
    children: [
      {
        index: true,
        loader: ({ params }) => redirect(`/${params.tournamentId}/live`),
      },
      {
        path: ':roundNumber',
        Component: RoundView,
      },
    ],
  },
]);

const useMock = !import.meta.env.SLOPE_APP_API_URL;

if (useMock) {
  console.warn('Using MOCK GraphQL API, please set SLOPE_APP_API_URL to use a real API');
}

const apolloClient = useMock
  ? createApolloMockClient()
  : createApolloClient({
      uri: import.meta.env.SLOPE_APP_API_URL,
      getToken: async () => {
        const token = await getToken(keycloak);
        return { token, tokenType: 'Bearer' };
      },
      onError: ({ graphQLErrors, networkError }) => {
        if (graphQLErrors) {
          graphQLErrors.forEach(({ message, locations, path }) => {
            console.error(
              `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
            );
            if (message.includes('token invalid')) {
              const toastId = 'token-invalid';
              toast.error(<ToastAuth keycloak={keycloak} message={message} toastId={toastId} />, {
                autoClose: false,
                toastId,
                closeButton: false,
              });
            } else {
              toast.error(`GraphQL error: ${message}`, { toastId: path?.join(',') });
            }
          });
        }

        if (networkError) {
          console.error(`[Network error]: ${networkError}`);
          toast.error(`Network error: ${networkError.message}`, { toastId: networkError.message });
        }
      },
    });

(async () => {
  createRoot(document.getElementById('root')!).render(
    <KeycloakProvider keycloak={keycloak}>
      <StrictMode>
        <App client={apolloClient} router={router} />
        <ToastContainer position={'bottom-right'} />
      </StrictMode>
    </KeycloakProvider>,
  );
})();
